import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { SunIcon, MoonIcon, ThermometerIcon, WindIcon, EyeOffIcon, EyeIcon, ChevronUpIcon, ChevronDownIcon, PowerIcon, SlidersIcon } from 'lucide-react';
interface ControlSliderProps {
  label: string;
  icon: React.ReactNode;
  value: number;
  onChange: (value: number) => void;
  unit?: string;
  min?: number;
  max?: number;
  color?: string;
}
function ControlSlider({
  label,
  icon,
  value,
  onChange,
  unit = '%',
  min = 0,
  max = 100,
  color = '#C9A84C'
}: ControlSliderProps) {
  return <div className="p-4 rounded-xl bg-[rgba(255,255,255,0.03)]" data-id="element-465">
      <div className="flex items-center justify-between mb-3" data-id="element-466">
        <div className="flex items-center gap-2" data-id="element-467">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{
          backgroundColor: `${color}20`
        }} data-id="element-468">
            {icon}
          </div>
          <span className="text-sm font-medium text-gray-300" data-id="element-469">{label}</span>
        </div>
        <span className="text-lg font-semibold" style={{
        color
      }} data-id="element-470">
          {value}
          {unit}
        </span>
      </div>
      <div className="relative" data-id="element-471">
        <input type="range" min={min} max={max} value={value} onChange={e => onChange(Number(e.target.value))} className="w-full h-2 rounded-full appearance-none cursor-pointer" style={{
        background: `linear-gradient(to right, ${color} 0%, ${color} ${(value - min) / (max - min) * 100}%, rgba(255,255,255,0.1) ${(value - min) / (max - min) * 100}%, rgba(255,255,255,0.1) 100%)`
      }} data-id="element-472" />
      </div>
    </div>;
}
interface ToggleControlProps {
  label: string;
  icon: React.ReactNode;
  activeIcon?: React.ReactNode;
  isActive: boolean;
  onToggle: () => void;
  color?: string;
}
function ToggleControl({
  label,
  icon,
  activeIcon,
  isActive,
  onToggle,
  color = '#C9A84C'
}: ToggleControlProps) {
  return <button onClick={onToggle} className={`p-4 rounded-xl transition-all touch-button h-auto flex-col items-start ${isActive ? 'border-2' : 'bg-[rgba(255,255,255,0.03)] hover:bg-[rgba(255,255,255,0.06)]'}`} style={isActive ? {
    backgroundColor: `${color}15`,
    borderColor: `${color}50`
  } : {}} data-id="element-473">
      <div className="flex items-center justify-between w-full mb-2" data-id="element-474">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{
        backgroundColor: isActive ? `${color}30` : 'rgba(255,255,255,0.05)'
      }} data-id="element-475">
          {isActive && activeIcon ? activeIcon : icon}
        </div>
        <div className={`w-12 h-6 rounded-full p-1 transition-colors ${isActive ? '' : 'bg-[rgba(255,255,255,0.1)]'}`} style={isActive ? {
        backgroundColor: color
      } : {}} data-id="element-476">
          <motion.div animate={{
          x: isActive ? 24 : 0
        }} className="w-4 h-4 rounded-full bg-white shadow-md" data-id="element-477" />
        </div>
      </div>
      <span className={`text-sm font-medium ${isActive ? 'text-white' : 'text-gray-400'}`} data-id="element-478">
        {label}
      </span>
      <span className="text-xs text-gray-500 mt-1" data-id="element-479">
        {isActive ? 'Active' : 'Off'}
      </span>
    </button>;
}
export function RoomControls() {
  const [lighting, setLighting] = useState(75);
  const [temperature, setTemperature] = useState(24);
  const [curtains, setCurtains] = useState(50);
  const [privacyGlass, setPrivacyGlass] = useState(true);
  const [acPower, setAcPower] = useState(true);
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    delay: 0.3
  }} className="glass-card rounded-2xl p-6 h-full flex flex-col" data-id="element-480">
      {/* Header */}
      <div className="flex items-center justify-between mb-6" data-id="element-481">
        <div className="flex items-center gap-3" data-id="element-482">
          <div className="w-10 h-10 rounded-xl bg-[rgba(201,168,76,0.15)] flex items-center justify-center" data-id="element-483">
            <SlidersIcon className="w-5 h-5 text-[#C9A84C]" data-id="element-484" />
          </div>
          <div data-id="element-485">
            <h3 className="text-lg font-semibold font-['Playfair_Display'] text-white" data-id="element-486">
              Room Controls
            </h3>
            <p className="text-xs text-gray-400" data-id="element-487">Voice enabled</p>
          </div>
        </div>
        <div className="flex items-center gap-2" data-id="element-488">
          <div className="w-2 h-2 rounded-full bg-green-400" data-id="element-489" />
          <span className="text-xs text-gray-400" data-id="element-490">All systems online</span>
        </div>
      </div>

      {/* Sliders */}
      <div className="space-y-4 mb-6" data-id="element-491">
        <ControlSlider label="Lighting" icon={<SunIcon className="w-4 h-4 text-yellow-400" data-id="element-493" />} value={lighting} onChange={setLighting} color="#FBBF24" data-id="element-492" />

        <ControlSlider label="Temperature" icon={<ThermometerIcon className="w-4 h-4 text-blue-400" data-id="element-495" />} value={temperature} onChange={setTemperature} unit="°C" min={16} max={30} color="#60A5FA" data-id="element-494" />

        <ControlSlider label="Curtains" icon={<ChevronUpIcon className="w-4 h-4 text-purple-400" data-id="element-497" />} value={curtains} onChange={setCurtains} color="#A78BFA" data-id="element-496" />
      </div>

      {/* Toggles */}
      <div className="grid grid-cols-2 gap-3 flex-1" data-id="element-498">
        <ToggleControl label="Privacy Glass" icon={<EyeIcon className="w-5 h-5 text-gray-400" data-id="element-500" />} activeIcon={<EyeOffIcon className="w-5 h-5 text-[#C9A84C]" data-id="element-501" />} isActive={privacyGlass} onToggle={() => setPrivacyGlass(!privacyGlass)} data-id="element-499" />

        <ToggleControl label="Air Conditioning" icon={<WindIcon className="w-5 h-5 text-gray-400" data-id="element-503" />} activeIcon={<WindIcon className="w-5 h-5 text-blue-400" data-id="element-504" />} isActive={acPower} onToggle={() => setAcPower(!acPower)} color="#60A5FA" data-id="element-502" />
      </div>

      {/* Quick Presets */}
      <div className="mt-6 pt-4 border-t border-[rgba(201,168,76,0.1)]" data-id="element-505">
        <p className="text-xs text-gray-500 mb-3" data-id="element-506">Quick Presets</p>
        <div className="flex gap-2" data-id="element-507">
          <button onClick={() => {
          setLighting(100);
          setCurtains(100);
          setPrivacyGlass(false);
        }} className="flex-1 touch-button h-12 rounded-xl bg-[rgba(255,255,255,0.05)] hover:bg-[rgba(255,255,255,0.1)] text-sm text-gray-300 transition-colors" data-id="element-508">
            <SunIcon className="w-4 h-4 mr-2 text-yellow-400" data-id="element-509" />
            Day Mode
          </button>
          <button onClick={() => {
          setLighting(30);
          setCurtains(0);
          setPrivacyGlass(true);
        }} className="flex-1 touch-button h-12 rounded-xl bg-[rgba(255,255,255,0.05)] hover:bg-[rgba(255,255,255,0.1)] text-sm text-gray-300 transition-colors" data-id="element-510">
            <MoonIcon className="w-4 h-4 mr-2 text-blue-400" data-id="element-511" />
            Presentation
          </button>
        </div>
      </div>
    </motion.div>;
}